import React from 'react';
import { noop } from 'lodash';
import { Comment, CommentDeleted } from '../comment';
import { unreachable } from '../../../ts-utils';
import { CommentBox } from '../comment-box';
import { useApi } from '../../context/api-context/use-api';
import { useConfiguration } from '../../context/configuration-context/use-configuration';
import { getIsMobile } from '../../../common/store/base-params/base-params-selectors';
import { isCommentContentEqual } from '../../../common/utils/content-utils';
import { isWithGuestFlow } from '../../../common/store/comments/comments-permissions';
import { useModal, DeleteCommentConfirmationModal } from '../modals';
import { getKey, getHtmlId } from './id-utils';
import ReplyingTo from '../replying-to/replying-to';
import { getCommentById } from '../../../common/store/comments/comments-selectors';
import { getOwner, getOwnerForReplyingTo } from '../../../common/store/members/members-selectors';
import { ActionsMenu } from '../actions-menu';
import { share } from './share';
import CommentErrorFooter from '../comment/footers/comment-error-footer';
import CommentPendingFooter from '../comment/footers/comment-pending-footer';
import { mapReplyState } from '../../../common/store/comments/replies/replies-selectors';
import { ReplyFooter } from '../comment/footers/comment-footer';
import classNames from 'classnames';
import { useTranslate } from '../../context/api-context/translations/use-translate';
import { resolveReplyingToText } from './reply-state-switch-utils';
import styles from './reply-state-switch.scss';
import { usePermissions } from '../../context/permissions-context/use-permissions';
const ReplyStateSwitchNoMemo = ({ replyState: replyStateIN, memberState }) => {
    const { getPermission } = usePermissions();
    const { resourceId, markLabels, hideReplyingTo, hideAvatars, ctxFields } = useConfiguration();
    const t = useTranslate();
    const replyState = mapReplyState(replyStateIN, memberState);
    const { isMobile, createReply, updateReply, cancelEditingReply, cancelCreatingReply, editReply, viewReplyTarget, biDiscardComment, biClickShowMoreBtn, editReplyCreateError, replyingToOwner, targetComment, owner, } = useApi((state, actions) => ({
        isMobile: getIsMobile(state),
        createReply: actions.createReply,
        updateReply: actions.updateReply,
        cancelEditingReply: actions.cancelEditingReply,
        cancelCreatingReply: actions.cancelCreatingReply,
        editReply: actions.editReply,
        viewReplyTarget: actions.viewReplyTarget,
        biDiscardComment: actions.biDiscardComment,
        replyingToOwner: getOwnerForReplyingTo(state, resourceId, replyStateIN),
        biClickShowMoreBtn: actions.biClickShowMoreBtn,
        editReplyCreateError: actions.editReplyCreateError,
        biClickPublishSelectBtn: actions.biClickPublishSelectBtn,
        owner: getOwner(replyState, state.members),
        targetComment: getCommentById(state, resourceId)(replyStateIN.targetId),
    }));
    const hasBeenUpdated = React.useRef(false);
    const replyingToText = resolveReplyingToText({
        targetComment,
        replyState,
        replyingToOwner,
        t,
    });
    React.useEffect(() => {
        switch (replyState.type) {
            case 'REPLY_UPDATE_PENDING':
                hasBeenUpdated.current = true;
                return;
            case 'REPLY_EDITING':
            case 'REPLY_CREATE_ERROR_EDITING':
            case 'REPLY_TYPING':
            case 'REPLY_READY':
            case 'REPLY_UPDATE_ERROR':
            case 'REPLY_DELETED':
            case 'REPLY_CREATE_PENDING':
            case 'REPLY_DELETE_PENDING':
            case 'REPLY_LIVE_PENDING':
            case 'REPLY_CREATE_ERROR':
                return;
            default: {
                throw unreachable(replyState);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [replyState.type]);
    switch (replyState.type) {
        case 'REPLY_TYPING': {
            const { localId, targetId, content } = replyState;
            return (React.createElement(CommentBox, { className: classNames(styles.commentBox, { [styles.withPadding]: hideAvatars }), key: getKey({ localId }), id: getHtmlId({ localId }), isReply: true, isEdit: false, isInList: true, formName: `comment-reply-${localId}`, initialContent: content, mountExpanded: true, withGuestFlow: isWithGuestFlow(getPermission({ action: 'CREATE_REPLY' })), onSubmit: ({ content }, _reset, asGuest) => {
                    createReply({
                        content,
                        resourceId,
                        ctxFields,
                        targetId,
                        localId,
                        asGuest,
                    });
                }, onCancel: (_content, _reset, isEmpty) => isEmpty
                    ? {
                        type: 'SILENT_CANCEL',
                        cleanup: () => cancelCreatingReply({ resourceId, targetId, localId }),
                    }
                    : {
                        type: 'PROMPT',
                        discard: 'COMMENT',
                        onConfirm: () => {
                            biDiscardComment({ type: 'reply', resourceId });
                            cancelCreatingReply({ resourceId, targetId, localId });
                        },
                    } }));
        }
        case 'REPLY_CREATE_ERROR': {
            const { localId, content, targetId, error, asGuest } = replyState;
            return (React.createElement(Comment, { key: getKey({ localId }), id: getHtmlId({ localId }), commentId: localId, content: content, owner: owner, isPending: false, isError: true, isReply: true, createdAt: new Date().toISOString(), replyingTo: !hideReplyingTo ? (React.createElement(ReplyingTo, { onClick: () => viewReplyTarget({
                        ctxFields,
                        resourceId,
                        targetCommentId: targetId,
                        sourceCommentId: localId,
                        targetStatus: replyState.targetStatus,
                        parentCommentId: replyState.parentId,
                    }), targetName: replyingToText })) : null, actionsMenu: null, disableShowMore: true, footer: React.createElement(CommentErrorFooter, { error: error, onEdit: () => editReplyCreateError({ resourceId, targetId, localId }), onRetry: () => createReply({
                        content,
                        resourceId,
                        ctxFields,
                        targetId,
                        localId,
                        asGuest,
                    }) }) }));
        }
        case 'REPLY_CREATE_ERROR_EDITING': {
            const { localId, content, targetId } = replyState;
            return (React.createElement(CommentBox, { className: classNames(styles.commentBox, { [styles.withPadding]: hideAvatars }), key: getKey({ localId }), id: getHtmlId({ localId }), commentId: localId, isReply: true, isEdit: false, isInList: true, formName: `comment-reply-${localId}`, initialContent: content, withGuestFlow: isWithGuestFlow(getPermission({ action: 'CREATE_REPLY' })), mountExpanded: true, onSubmit: ({ content }, _reset, asGuest) => {
                    createReply({
                        content,
                        resourceId,
                        ctxFields,
                        targetId,
                        localId,
                        asGuest,
                    });
                }, onCancel: (_content, _reset, isEmpty) => isEmpty
                    ? {
                        type: 'SILENT_CANCEL',
                        cleanup: () => cancelCreatingReply({ resourceId, targetId, localId }),
                    }
                    : {
                        type: 'PROMPT',
                        discard: 'COMMENT',
                        onConfirm: () => {
                            biDiscardComment({ type: 'reply', resourceId });
                            cancelCreatingReply({ resourceId, targetId, localId });
                        },
                    } }));
        }
        case 'REPLY_CREATE_PENDING': {
            const { localId, content } = replyState;
            return (React.createElement(Comment, { key: getKey({ localId }), id: getHtmlId({ localId }), commentId: localId, content: content, owner: owner, isPending: true, isReply: true, createdAt: new Date().toISOString(), replyingTo: !hideReplyingTo ? React.createElement(ReplyingTo, { onClick: noop, targetName: replyingToText }) : null, actionsMenu: React.createElement(ActionsMenu, { showDummy: true }), disableShowMore: true, footer: React.createElement(CommentPendingFooter, null) }));
        }
        case 'REPLY_UPDATE_ERROR': {
            const { marked, localId, content, targetId, error, commentId, comment } = replyState;
            return (React.createElement(Comment, { key: getKey({ localId, commentId }), id: getHtmlId({ localId, commentId }), commentId: commentId, content: content, owner: owner, isPending: false, isError: true, isMarked: marked.isMarked, isEdited: comment.isEdited, isReply: true, createdAt: comment.createdAt, replyingTo: !hideReplyingTo ? (React.createElement(ReplyingTo, { onClick: () => viewReplyTarget({
                        resourceId,
                        ctxFields,
                        targetCommentId: targetId,
                        sourceCommentId: commentId,
                        targetStatus: replyState.targetStatus,
                        parentCommentId: replyState.parentId,
                    }), targetName: replyingToText, isPending: replyState.replyingToTargetLoading })) : null, actionsMenu: React.createElement(ActionsMenuReplyReady, { commentId: commentId, comment: comment, isMobile: isMobile, marked: marked }), disableShowMore: true, footer: React.createElement(CommentErrorFooter, { error: error, onEdit: () => {
                        editReply({ resourceId, commentId });
                    }, onRetry: () => updateReply({
                        content,
                        resourceId,
                        ctxFields,
                        commentId,
                        revisionId: comment.revisionId,
                        currentComment: comment,
                    }) }) }));
        }
        case 'REPLY_READY': {
            const { marked, comment, localId, commentId, targetId, reactions } = replyState;
            if (comment.status === 'DELETED') {
                return (React.createElement(CommentDeleted, { key: getKey({ localId, commentId }), id: getHtmlId({ localId, commentId }), isReply: true }));
            }
            return (React.createElement(Comment, { key: getKey({ localId, commentId }), id: getHtmlId({ localId, commentId }), commentId: commentId, content: comment.content?.richContent, owner: owner, createdAt: comment.createdAt, isMarked: marked.isMarked, customBadgeLabel: markLabels?.badgeLabel, customBadgeIcon: markLabels?.icon, replyingTo: !hideReplyingTo ? (React.createElement(ReplyingTo, { onClick: () => viewReplyTarget({
                        resourceId,
                        ctxFields,
                        targetCommentId: targetId,
                        sourceCommentId: commentId,
                        targetStatus: replyState.targetStatus,
                        parentCommentId: replyState.parentId,
                    }), targetName: replyingToText, isPending: replyState.replyingToTargetLoading })) : null, isReply: true, isLive: replyState.isLive, isPending: marked.pending, isEdited: comment.isEdited, actionsMenu: React.createElement(ActionsMenuReplyReady, { commentId: commentId, comment: comment, isMobile: isMobile, marked: marked }), onShowMoreClick: (isShowMore) => {
                    biClickShowMoreBtn({ isShowMore, resourceId, commentId });
                }, disableShowMore: !!localId || hasBeenUpdated.current, footer: React.createElement(ReplyFooter, { commentId: commentId, reactionsState: reactions, canReact: getPermission({ action: 'REACT' }).type === 'ALLOW', replyButtonState: getPermission({ action: 'CREATE_REPLY' }).type === 'ALLOW' ? 'enabled' : 'hidden' }) }));
        }
        case 'REPLY_EDITING': {
            const { marked, comment, content, commentId, localId } = replyState;
            const initialContent = content || comment.content?.richContent;
            return (React.createElement(CommentBox, { className: classNames(styles.commentBox, { [styles.withPadding]: hideAvatars }), key: getKey({ localId, commentId }), id: getHtmlId({ localId, commentId }), commentId: commentId, isReply: true, isEdit: true, isInList: true, isMarked: marked.isMarked, initialContent: initialContent, formName: `comment-reply-${comment.commentId}`, mountExpanded: true, onSubmit: ({ content }) => {
                    updateReply({
                        content,
                        resourceId,
                        commentId,
                        ctxFields,
                        revisionId: comment.revisionId,
                        currentComment: comment,
                    });
                }, onCancel: (content) => isCommentContentEqual(content, comment.content?.richContent)
                    ? {
                        type: 'SILENT_CANCEL',
                        cleanup: () => cancelEditingReply({ resourceId, commentId }),
                    }
                    : {
                        type: 'PROMPT',
                        discard: 'CHANGES',
                        onConfirm: () => {
                            biDiscardComment({ type: 'reply', resourceId });
                            cancelEditingReply({ resourceId, commentId });
                        },
                    } }));
        }
        case 'REPLY_DELETED': {
            const { localId, commentId } = replyState;
            return (React.createElement(CommentDeleted, { key: getKey({ localId, commentId }), id: getHtmlId({ localId, commentId }), isReply: true }));
        }
        case 'REPLY_UPDATE_PENDING': {
            const { content, marked, localId, commentId, comment } = replyState;
            return (React.createElement(Comment, { key: getKey({ localId, commentId }), id: getHtmlId({ localId, commentId }), commentId: commentId, content: content, owner: owner, isMarked: marked.isMarked, isPending: true, isEdited: comment.isEdited, isReply: true, createdAt: comment.createdAt, replyingTo: !hideReplyingTo ? React.createElement(ReplyingTo, { onClick: noop, targetName: replyingToText }) : null, actionsMenu: React.createElement(ActionsMenu, { showDummy: true }), disableShowMore: true, footer: React.createElement(CommentPendingFooter, null) }));
        }
        case 'REPLY_LIVE_PENDING': {
            const { commentId, content } = replyState;
            return (React.createElement(Comment, { key: getKey({ commentId }), id: getHtmlId({ commentId }), commentId: commentId, content: content, owner: owner, isPending: true, isReply: true, isLive: true, replyingTo: !hideReplyingTo ? (React.createElement(ReplyingTo, { onClick: () => { }, targetName: replyingToText, isPending: false })) : null, createdAt: new Date().toISOString(), actionsMenu: React.createElement(ActionsMenu, { showDummy: true }), footer: React.createElement(ReplyFooter, { commentId: replyState.commentId, reactionsState: { total: 0, pending: false, reactions: [] }, canReact: false, replyButtonState: getPermission({ action: 'CREATE_REPLY' }).type === 'ALLOW' ? 'disabled' : 'hidden' }) }));
        }
        case 'REPLY_DELETE_PENDING': {
            const { localId, commentId } = replyState;
            return (React.createElement(CommentDeleted, { isPending: true, isReply: true, key: getKey({ commentId, localId }), id: getHtmlId({ commentId, localId }) }));
        }
        default: {
            throw unreachable(replyState);
        }
    }
};
export const ReplyStateSwitch = React.memo(ReplyStateSwitchNoMemo);
const ActionsMenuReplyReady = ({ marked, commentId, comment, isMobile }) => {
    const { getPermission } = usePermissions();
    const { resourceId, deepLink, _showReportAction } = useConfiguration();
    const showModal = useModal();
    const { editReply, deleteReply, markComment, biSelectCommentAction, biOpenCommentActions, showToast, reportComment, } = useApi((_state, actions) => ({
        editReply: actions.editReply,
        deleteReply: actions.deleteReply,
        markComment: actions.markComment,
        biSelectCommentAction: actions.biSelectCommentAction,
        biOpenCommentActions: actions.biOpenCommentActions,
        showToast: actions.showToast,
        reportComment: actions.reportComment,
    }));
    return (React.createElement(ActionsMenu, { onShowMenu: () => biOpenCommentActions({
            type: 'reply',
            resourceId,
            commentId,
        }), edit: getPermission({ action: 'UPDATE', comment }).type === 'ALLOW'
            ? () => {
                biSelectCommentAction({
                    type: 'reply',
                    action: 'edit',
                    resourceId,
                    commentId,
                });
                editReply({
                    resourceId,
                    commentId,
                });
            }
            : undefined, mark: getPermission({ action: 'MARK' }).type === 'ALLOW' && marked && !marked.pending
            ? {
                isMarked: marked.isMarked,
                action: () => {
                    biSelectCommentAction({
                        type: 'reply',
                        action: marked.isMarked ? 'unmark' : 'mark',
                        resourceId,
                        commentId,
                    });
                    markComment({
                        resourceId,
                        commentId,
                        subaction: marked.isMarked ? 'REMOVE' : 'ADD',
                    });
                },
            }
            : undefined, delete: getPermission({ action: 'DELETE', comment }).type === 'ALLOW'
            ? () => {
                biSelectCommentAction({
                    type: 'reply',
                    action: 'delete',
                    resourceId,
                    commentId,
                });
                showModal((toggle) => (React.createElement(DeleteCommentConfirmationModal, { isReply: true, toggle: toggle, onClose: (confirmed) => {
                        confirmed &&
                            deleteReply({
                                resourceId,
                                commentId,
                            });
                    } })));
            }
            : undefined, share: deepLink
            ? () => {
                biSelectCommentAction({
                    type: 'reply',
                    action: 'share',
                    resourceId,
                    commentId,
                });
                share({
                    url: deepLink.generateLink(commentId),
                    isMobile,
                    afterCopy: () => showToast({
                        skin: 'success',
                        message: { type: 'TRANSLATION', key: 'toast.share-success' },
                        resourceId,
                    }),
                });
            }
            : undefined, report: getPermission({ action: 'REPORT' }).type === 'ALLOW' && _showReportAction
            ? () => reportComment({ commentId, resourceId, isReply: true })
            : undefined }));
};
//# sourceMappingURL=reply-state-switch.js.map